import { useState } from "react";
import { Input, notification } from "antd";
import FolderIcon from "@/assets/icons/folder_off.svg?react";
import AvatarWithInitials from "@/components/UI/AvatarWithInitials";
import { formatDateTimeWithAt } from "@/utils/dateUtils";
import { useScopeParams } from "@/hooks/useScopeParams";
import trialDataApiCRUDServices from "@/services/trialDataApiCRUDServices";
import FetchWrapper from "@/components/UI/FetchWraper";

const { TextArea } = Input;

type Comment = {
  id: number;
  author_name: string;
  text: string;
  created_at: string;
};

type CommentsData = {
  count: number;
  results: Comment[];
};

type Props = {
  discrepancyId: string;
  refetchTrigger?: number;
};

export default function DiscrepancyComments({
  discrepancyId,
  refetchTrigger,
}: Props) {
  return (
    <FetchWrapper
      endpointSuffix={`issue_management/comments/${discrepancyId}/`}
      extraQueryKey={refetchTrigger?.toString() ?? ""}
    >
      {(data: CommentsData) => (
        <DiscrepancyCommentsContent data={data} discrepancyId={discrepancyId} />
      )}
    </FetchWrapper>
  );
}

function DiscrepancyCommentsContent({
  data,
  discrepancyId,
}: {
  data: CommentsData;
  discrepancyId: string;
}) {
  const { id: trialId } = useScopeParams();
  const [text, setText] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [localComments, setLocalComments] = useState<Comment[]>([]);

  const allComments = [...(data.results ?? []), ...localComments];

  const handleSubmit = async () => {
    const trimmed = text.trim();
    if (!trimmed) return;
    setSubmitting(true);
    try {
      const response = await trialDataApiCRUDServices.create(
        `${trialId}/issue_management/comments/${discrepancyId}`,
        { text: trimmed },
      );
      if (!response.ok) {
        const body = await response.json().catch(() => ({}));
        throw new Error(body?.error ?? `Error ${response.status}`);
      }
      const created: Comment = await response.json();
      setLocalComments((prev) => [...prev, created]);
      setText("");
    } catch (err) {
      notification.error({
        message: "Failed to post comment",
        description: err instanceof Error ? err.message : "Unknown error",
        duration: 5,
      });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="space-y-4">
      {allComments.length === 0 ? (
        <div className="flex flex-col items-center mt-4 h-16 text-[#00000073]">
          <FolderIcon />
          No comments yet
        </div>
      ) : (
        <div className="space-y-4">
          {allComments.map((c) => (
            <div key={c.id} className="flex gap-3">
              <AvatarWithInitials
                name={c.author_name}
                avatarSize={28}
                labelFontSize={14}
              />
              <div className="flex-1 min-w-0">
                <div className="flex items-baseline gap-2">
                  <span className="text-sm font-medium text-[#2B4752]">
                    {c.author_name}
                  </span>
                  <span className="text-xs text-[#8C8C8C]">
                    {c.created_at ? formatDateTimeWithAt(c.created_at) : ""}
                  </span>
                </div>
                <p className="text-sm text-[#2B4752] mt-0.5 whitespace-pre-wrap break-words">
                  {c.text}
                </p>
              </div>
            </div>
          ))}
        </div>
      )}

      <div className="pt-2">
        <TextArea
          placeholder="Add a comment..."
          value={text}
          onChange={(e) => setText(e.target.value)}
          rows={2}
          className="text-sm"
          onKeyDown={(e) => {
            if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) handleSubmit();
          }}
        />
        <div className="flex justify-end mt-1.5">
          <button
            onClick={handleSubmit}
            disabled={submitting || !text.trim()}
            className="px-3 py-1 text-xs rounded-md bg-[#003832] text-white hover:bg-[#004A42] transition-colors disabled:opacity-50"
          >
            {submitting ? "Posting…" : "Post"}
          </button>
        </div>
        <p className="text-[10px] text-[#8C8C8C] mt-1">⌘+Enter to submit</p>
      </div>
    </div>
  );
}
